import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import BatchSelector from '../../../Components/BatchSelector';
import { showToast, showConfirm } from '../../../Components/ToastNotifications';

const MentorUsersList = () => {
  const { authUser } = useAuth();
  const { batchId: batchdIdLoggedIn } = authUser?.activeSession || {};
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get('page')) || 1,
  );
  const [pageSize, setPageSize] = useState(
    Number(searchParams.get('pageSize')) || 25,
  );
  const [batchId, setBatchId] = useState(
    Number(searchParams.get('batch')) || 2,
  );

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState({ users: [], total: 0 });

  useEffect(() => {
    const getAllUsers = async () => {
      setLoading(true);
      const data = await api.getAllUsers({
        batchId,
        page: currentPage,
        pageSize,
      });
      setUsers(data);
      setLoading(false);
    };

    getAllUsers();
  }, [batchId, currentPage, pageSize]);

  const handlePageSizeChange = (size) => {
    const newSize = Number(size);
    setPageSize(newSize);
    setCurrentPage(1); // reset ke halaman 1
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: 1,
      pageSize: newSize,
      batch: batchId,
    });
    navigate(`?batch=${batchId}&page=1&pageSize=${newSize}`);
  };

  const handleBatchChange = (id) => {
    const newBatchId = Number(id);
    setBatchId(newBatchId);
    setCurrentPage(1);
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      batch: newBatchId,
      page: 1,
      pageSize,
    });
    navigate(`?batch=${newBatchId}&page=1&pageSize=${pageSize}`);
  };

  const handleNavigateToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: pageNumber,
    });
    navigate(`?batch=${batchId}&page=${pageNumber}&pageSize=${pageSize}`);
  };

  const onAssignStudentsToMentors = ({ id, name }) => {
    showConfirm({
      message: `Apakah Anda yakin ingin membimbing mahasiswa atas nama ${name}? Tindakan ini akan menjadikan Anda sebagai mentor resminya.`,
      type: 'primary',
      onConfirm: async () => {
        try {
          const { status, message } = await api.assignStudentsEvaluation({
            id,
          });
          if (status !== 'success') {
            showToast(message || `Maaf, proses gagal. ${name} mungkin sudah dibimbing oleh mentor lain.`, 'warning');
          } else {
            showToast(`Berhasil! ${name} kini resmi menjadi mentee Anda.`, 'success');
            navigate('/mentor/mentees/evaluations');
          }
        } catch (error) {
          const errorMessage = error?.response?.data?.message || error?.message || 'Terjadi kesalahan sistem saat memproses permintaan.';
          showToast(`Gagal: ${errorMessage}`, 'danger');
        }
      }
    });
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      {/* Header Section */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-people-fill text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Daftar Mahaswiswa</h1>
            <p
              className='text-muted mb-0 d-none d-lg-block'
              style={{ fontSize: '0.75rem' }}
            >
              Kelola data dan pantau seluruh mahasiswa yang terdaftar.
            </p>
          </div>
        </div>

        <div className='d-flex flex-wrap align-items-center gap-3 ms-auto'>
          {/* Page Size Selector */}
          <div className='d-flex align-items-center'>
            <label className='me-2 small text-muted'>Tampilkan:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className='form-select form-select-sm shadow-sm rounded-pill px-3'
              style={{ minWidth: '80px', fontSize: '0.8rem' }}
            >
              {[25, 50, 100].map((size) => (
                <option
                  key={size}
                  value={size}
                >
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Batch Filter Selector */}
          <div className='d-flex align-items-center ms-1'>
            <label className='me-2 fw-medium text-muted small mb-0 d-none d-xl-inline'>
              Batch:
            </label>
            <div style={{ minWidth: '150px' }}>
              <BatchSelector
                value={batchId}
                onChange={handleBatchChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='table-responsive'>
        <table className='table table-hover align-middle mb-0'>
          <thead className='bg-light text-muted small text-uppercase fw-bold'>
            <tr>
              <th className='ps-4 py-3 border-0' style={{ width: '50px' }}>#</th>
              <th className='py-3 border-0'>Nama Lengkap</th>
              <th className='py-3 border-0'>Lembaga</th>
              <th className='py-3 border-0'>Penempatan</th>
              <th className='py-3 border-0'>Universitas</th>
              <th className='py-3 border-0'>Kategori</th>
              <th className='pe-4 py-3 border-0' style={{ width: '120px' }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr
                  key={i}
                  className='placeholder-glow'
                >
                  <td className='ps-4 py-3'>
                    <span className='placeholder col-6 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-10 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-8 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-8 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-10 rounded'></span>
                  </td>
                  <td className='py-3'>
                    <span className='placeholder col-6 rounded'></span>
                  </td>
                  <td className='pe-4 py-3'>
                    <span className='placeholder col-12 rounded py-3'></span>
                  </td>
                </tr>
              ))
            ) : (
              users?.users?.map((value, key) => (
                <tr
                  key={key}
                  className='transition-all'
                  style={{ cursor: 'default' }}
                >
                  <td className='ps-4 text-muted small py-3'>
                    {(currentPage - 1) * pageSize + (key + 1)}
                  </td>
                  <td className='py-3'>
                    <div className='fw-bold text-dark'>
                      {value.firstName} {value.lastName}
                    </div>
                  </td>
                  <td className='py-3'>
                    <div
                      className={`extra-small ${value?.institutions?.institutionsName ? 'fw-bold text-primary' : 'text-muted fst-italic'}`}
                    >
                      {value?.institutions?.institutionsName ||
                        'Belum diatur'}
                    </div>
                  </td>
                  <td className='py-3'>
                    <div className='d-flex flex-column'>
                      <span
                        className={`fw-semibold ${value?.institutions?.institutionsPronvinciesNames ? 'text-dark' : 'text-muted fst-italic fw-normal'} extra-small`}
                      >
                        {value?.institutions?.institutionsPronvinciesNames || 'Belum diatur'}
                      </span>
                      {value?.institutions?.institutionsRegenciesNames && (
                        <span
                          className='text-muted'
                          style={{ fontSize: '0.65rem' }}
                        >
                          {value?.institutions?.institutionsRegenciesNames}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className='py-3'>
                    <div
                      className={`extra-small ${value?.universities?.universitiesName ? 'text-dark fw-medium' : 'text-muted fst-italic'}`}
                    >
                      {value?.universities?.universitiesName ||
                        'Belum diatur'}
                    </div>
                  </td>
                  <td className='py-3'>
                    <span className='badge bg-light text-muted border extra-small fw-normal rounded-pill px-2 py-1'>
                      {value.program.thirdTierProgramName}
                    </span>
                  </td>
                  <td className='pe-4 py-3'>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAssignStudentsToMentors({
                          id: value.CLPUsersId,
                          name: `${value.firstName} ${value.lastName}`,
                        });
                      }}
                      className='btn btn-primary btn-sm rounded-pill px-3 extra-small fw-bold border-0 shadow-sm'
                    >
                      Tambah
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && users?.users?.length > 0 && (
        <div className='mt-4 pt-4 border-top'>
          <nav aria-label='Users navigation'>
            <ul className='pagination pagination-sm justify-content-center mb-0 gap-2'>
              <li className={`page-item ${!users.previous ? 'disabled' : ''}`}>
                <button
                  onClick={() => handleNavigateToPage(users.previous)}
                  className='page-link rounded-pill border-0 bg-light text-dark px-3 fw-bold'
                >
                  <i className='bi bi-chevron-left'></i>
                </button>
              </li>
              <li className='page-item active'>
                <span className='page-link rounded-pill border-0 bg-primary text-white px-3 fw-bold shadow-sm'>
                  {users.current + 1 || parseInt(currentPage)}
                </span>
              </li>
              <li className={`page-item ${!users.next ? 'disabled' : ''}`}>
                <button
                  onClick={() => handleNavigateToPage(users.next + 1)}
                  className='page-link rounded-pill border-0 bg-light text-dark px-3 fw-bold'
                >
                  <i className='bi bi-chevron-right'></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      <style>{`
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
        .table-hover tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.03) !important;
          transform: scale(0.998);
        }
      `}</style>
    </div>
  );
};

export default MentorUsersList;
